<template>
    <kiosk-org-unit-details :teamId="props.teamId" :orgUnitId="props.orgUnitId">
        <div class="flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-org-unit-billing-details" class="mb-4" />

                <page-header>Billing Details</page-header>

                <page-loading v-if="!billing"></page-loading>
                <template v-else>
                    <org-unit-billing-email
                        class="mb-4"
                        :teamId="orgUnit.team_id"
                        :orgUnitId="orgUnit.id"
                        :billingEmail="billing.billing_email"
                        :isEditable="isEditable"
                        :allowUnset="true"
                        :unsetting="unsettingEmail"
                        @unset="confirmUnsetEmail"
                    />

                    <org-unit-billing-info
                        class="mb-4"
                        :teamId="orgUnit.team_id"
                        :orgUnitId="orgUnit.id"
                        :billingInformation="{
                            billing_company_legal_name: billing.billing_company_legal_name,
                            billing_address: billing.billing_address,
                            billing_address_line_2: billing.billing_address_line_2,
                            billing_city: billing.billing_city,
                            billing_state: billing.billing_state,
                            billing_zip: billing.billing_zip,
                            billing_country: billing.billing_country,
                            billing_po_number: billing.billing_po_number,
                        }"
                        :isEditable="isEditable"
                        :allowUnset="true"
                        :unsetting="unsettingInformation"
                        @unset="confirmUnsetInformation"
                    />

                    <org-unit-stripe-customer
                        v-if="stripeKey && isEditable"
                        class="mb-4"
                        :stripeId="billing.stripe_id"
                    />
                </template>
            </div>
        </div>
    </kiosk-org-unit-details>
</template>

<script setup>
import { VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import KioskOrgUnitDetails from '~/components/kiosk/kiosk-org-units/details/KioskOrgUnitDetails.vue';
import { useOrgUnitDetails } from '~/components/kiosk/kiosk-org-units/composables/useOrgUnitDetails';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import OrgUnitBillingEmail from '~/components/settings/billing/OrgUnitBillingEmail.vue';
import OrgUnitBillingInfo from '~/components/settings/billing/OrgUnitBillingInfo.vue';
import OrgUnitStripeCustomer from '~/components/settings/billing/OrgUnitStripeCustomer.vue';
import { useToasted } from '~/composables/useToasted';
import { adminOrgUnitsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';

const { orgUnit, billing, getBilling, onMountedOrgUnit } = useOrgUnitDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const stripeKey = Spark.stripeKey;
const isEditable = computed(() => orgUnit.value.is_billable);

const toasted = useToasted();
const unsettingInformation = ref(false);
const unsettingEmail = ref(false);

const confirmUnsetInformation = () => {
    VxSwal.confirmation(
        'Unset billing information',
        'This permanently clears the billing address, company name, and PO number for this organizational unit. This action cannot be undone.',
        'danger',
        () => unsetInformation()
    );
};

const unsetInformation = async () => {
    unsettingInformation.value = true;
    try {
        await adminOrgUnitsClient.unsetBillingInformation(props.teamId, props.orgUnitId);
        await getBilling();
        toasted.success('Billing information cleared.');
    } catch (e) {
        throwSwalRequestError(e);
    } finally {
        unsettingInformation.value = false;
    }
};

const confirmUnsetEmail = () => {
    VxSwal.confirmation(
        'Unset billing email',
        'This permanently clears the billing email for this organizational unit. This action cannot be undone.',
        'danger',
        () => unsetEmail()
    );
};

const unsetEmail = async () => {
    unsettingEmail.value = true;
    try {
        await adminOrgUnitsClient.unsetBillingEmail(props.teamId, props.orgUnitId);
        await getBilling();
        toasted.success('Billing email cleared.');
    } catch (e) {
        throwSwalRequestError(e);
    } finally {
        unsettingEmail.value = false;
    }
};

onMountedOrgUnit(() => {
    getBilling();
});
</script>
